/**
 * TanStack Query hooks for authentication.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { authApi } from '../api';

// ============================================================================
// Query Keys
// ============================================================================

export const authKeys = {
  all: ['auth'] as const,
  apiKey: () => [...authKeys.all, 'api-key'] as const,
};

// ============================================================================
// Auth Hooks
// ============================================================================

/**
 * Fetch the current user's API key.
 * Used for MCP server configuration and programmatic access.
 */
export function useApiKey() {
  return useQuery({
    queryKey: authKeys.apiKey(),
    queryFn: () => authApi.getApiKey(),
  });
}

/**
 * Update the current user's display name (AB#463). The caller should refresh the
 * auth store (`checkAuth`) on success so cached user labels update.
 */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: (params: { name: string }) => authApi.updateProfile(params),
  });
}

/**
 * Change the current user's password (AB#463). Rejects a wrong current password
 * with 400 `current_password_invalid` — surface the message, don't assume success.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (params: { current_password: string; new_password: string }) =>
      authApi.changePassword(params),
  });
}
